/* eslint-disable react/prop-types */
import { useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Lock,
  Mail,
  MapPin,
  Phone,
  Save,
  User,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import { SUBCITIES } from "../../../utils/constants";

const INITIAL_FORM = {
  name: "",
  email: "",
  phoneNumber: "",
  alternativePhone: "",
  addressLine1: "",
  addressLine2: "",
  subcity: "",
  gender: "Not Selected",
  dob: "",
  password: "",
};

const Field = ({
  label,
  name,
  type = "text",
  required = false,
  icon,
  formData,
  errors,
  onChange,
  inputClass,
}) => (
  <div>
    <label className="mb-2 block text-sm font-semibold text-text dark:text-dark-text">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {icon && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {icon}
        </span>
      )}
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={onChange}
        className={`${inputClass(name)} ${icon ? "pl-10" : ""}`}
      />
    </div>
    {errors[name] && (
      <p className="mt-1.5 flex items-center gap-1 text-sm text-red-500">
        <AlertCircle size={14} /> {errors[name]}
      </p>
    )}
  </div>
);

const AddClient = () => {
  const navigate = useNavigate();
  const { createClient } = useContext(AdminContext);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (errors[name] || errors.form) {
      setErrors((current) => ({ ...current, [name]: "", form: "" }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = "Name is required";
    if (!formData.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email.trim())) {
      nextErrors.email = "Please enter a valid email address";
    }
    if (!formData.phoneNumber.trim()) {
      nextErrors.phoneNumber = "Phone number is required";
    }
    if (!formData.addressLine1.trim()) {
      nextErrors.addressLine1 = "Address line 1 is required";
    }
    if (!formData.subcity) nextErrors.subcity = "Subcity is required";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitLockRef.current) return;
    if (!validate()) return;

    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      const result = await createClient({
        ...formData,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber.trim(),
        alternativePhone: formData.alternativePhone.trim(),
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2.trim(),
        subcity: formData.subcity.trim(),
      });

      if (result?.success) navigate("/clients");
      else if (result?.message) setErrors({ form: result.message });
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full rounded-xl border bg-white dark:bg-gray-900 px-4 py-3 text-text dark:text-dark-text outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 ${
      errors[field]
        ? "border-red-500"
        : "border-gray-200 dark:border-dark-border"
    }`;

  const fieldProps = {
    formData,
    errors,
    onChange: handleChange,
    inputClass,
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12">
      <button
        type="button"
        onClick={() => navigate("/clients")}
        className="mb-6 flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-primary dark:text-dark-muted"
      >
        <ArrowLeft size={18} /> Back to Clients
      </button>

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text dark:text-dark-text sm:text-3xl">
          Add Client
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-dark-muted sm:text-base">
          Create a client profile with their contact and address details.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="max-w-4xl rounded-3xl border border-gray-100 bg-card p-6 shadow-sm dark:border-dark-border dark:bg-dark-surface sm:p-8"
      >
        {errors.form && (
          <div className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
            <AlertCircle size={18} /> {errors.form}
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-2">
          <Field
            {...fieldProps}
            label="Name"
            name="name"
            required
            icon={<User size={18} />}
          />
          <Field
            {...fieldProps}
            label="Email"
            name="email"
            type="email"
            required
            icon={<Mail size={18} />}
          />
          <Field
            {...fieldProps}
            label="Phone Number"
            name="phoneNumber"
            required
            icon={<Phone size={18} />}
          />
          <Field
            {...fieldProps}
            label="Alternative Phone"
            name="alternativePhone"
            icon={<Phone size={18} />}
          />
          <div className="md:col-span-2">
            <Field
              {...fieldProps}
              label="Address Line 1"
              name="addressLine1"
              required
              icon={<MapPin size={18} />}
            />
          </div>
          <Field {...fieldProps} label="Address Line 2" name="addressLine2" />
          <div>
            <label className="mb-2 block text-sm font-semibold text-text dark:text-dark-text">
              Subcity <span className="text-red-500">*</span>
            </label>
            <select
              name="subcity"
              value={formData.subcity}
              onChange={handleChange}
              className={inputClass("subcity")}
            >
              <option value="">Select subcity</option>
              {SUBCITIES.map((subcity) => (
                <option key={subcity} value={subcity}>
                  {subcity}
                </option>
              ))}
            </select>
            {errors.subcity && (
              <p className="mt-1.5 flex items-center gap-1 text-sm text-red-500">
                <AlertCircle size={14} /> {errors.subcity}
              </p>
            )}
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold text-text dark:text-dark-text">
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              className={inputClass("gender")}
            >
              <option value="Not Selected">Not Selected</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
          <Field {...fieldProps} label="Date of Birth" name="dob" type="date" />
          <div>
            <Field
              {...fieldProps}
              label="Initial Password (optional)"
              name="password"
              type="password"
              icon={<Lock size={18} />}
            />
            <p className="mt-1.5 text-xs text-gray-500 dark:text-dark-muted">
              If blank, the phone number will be used as the initial password.
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col-reverse gap-3 border-t border-gray-100 pt-6 dark:border-dark-border sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate("/clients")}
            className="rounded-xl bg-gray-100 px-6 py-3 font-medium text-text transition hover:bg-gray-200 dark:bg-gray-800 dark:text-dark-text dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3 font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save size={18} />
            {isSubmitting ? "Creating..." : "Create Client"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddClient;
